import type { JSONSchemaNode } from '@/lib/api/types';
import {
  CLIENT_TYPES,
  DEPARTMENTS,
  ESTIMATOR_ROLES,
  DEVICE_TYPES,
  FIELD_NOTE_BLOCK_TYPES,
  INDUSTRY_KEYS,
  INSIGHT_TOPICS,
  PACKAGE_CATEGORIES,
  PACKAGE_KINDS,
  PORTFOLIO_KINDS,
  PRICE_UNITS,
  SERVICE_ICONS,
  SERVICE_TIERS,
  enumLabels,
  type VocabOption,
} from './vocab';

/**
 * Content-collection registry. One config-driven engine serves every collection;
 * adding a collection is a single entry here. `docSchema` is the console-shipped,
 * per-collection doc form definition — but it is only the **offline fallback**: the
 * backend serves the authoritative schema at `/admin/collection-schemas` and wins at
 * runtime (SchemaForm renders whatever JSON Schema it gets). Keep this in sync with
 * the Phase-1 docschema so the offline editor matches the live one.
 *
 * Y/DIRECTION (CMS plan D4) ships exactly two collections: `field-notes` (the
 * thought-leadership essays — the one true V1 collection) and `site-assets` (the
 * key-addressed media slots). The eight legacy collections are dropped; every other
 * page is page-copy (D1), authored in Settings → Page copy.
 */
export interface FacetDef {
  key: string;
  label: string;
  /**
   * Facet kind. Y/DIRECTION's V1 collections declare no facets, but the generic
   * engine (ContentListPage / SchemaForm) still supports the full set — keep them
   * so the offline filter UI typechecks against the authoritative backend schema.
   */
  type: 'text' | 'boolean' | 'enum' | 'ref-slug';
  /** Static options for `enum` facets. */
  options?: VocabOption[];
  /** Collection key for `ref-slug` facets. */
  refCollection?: string;
}

export interface CollectionConfig {
  /** route segment under /admin and the API path segment. */
  key: string;
  /** i18n nav key. */
  navKey: string;
  /** Display label (EN fallback for chrome). */
  label: string;
  /** Singular i18n label key — used for the "New {singular}" editor heading. */
  singularLabel: string;
  identType: 'slug' | 'key';
  hasSeo: boolean;
  /** When true, items can only be edited — no create or delete (a fixed register of records). */
  editOnly?: boolean;
  facets: FacetDef[];
  /** JSON-Schema for the bilingual `doc` form. */
  docSchema: JSONSchemaNode;
}

const I18N: JSONSchemaNode = { $ref: '#/$defs/I18n' };
const I18N_RICH: JSONSchemaNode = { $ref: '#/$defs/I18nRich' };

const DEFS = {
  I18n: { type: 'object', properties: { en: { type: 'string' }, ar: { type: 'string' } } },
  I18nRich: { type: 'object', properties: { en: { type: 'string' }, ar: { type: 'string' } } },
} as const;

export const SCHEMA_DEFS = DEFS as unknown as Record<string, JSONSchemaNode>;

const enumField = (
  options: VocabOption[],
  title: string,
  hint?: string,
): JSONSchemaNode => ({
  type: 'string',
  title,
  enum: options.map((o) => o.value),
  'x-enum-labels': enumLabels(options),
  ...(hint ? { 'x-hint': hint } : {}),
});

/** Whole-dollar amount, nullable (backend `money()`: no ceiling / not priced). */
const money = (title: string, hint?: string): JSONSchemaNode => ({
  type: ['integer', 'null'],
  minimum: 0,
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/** Non-negative integer estimator parameter (backend `count()`). */
const count = (title: string): JSONSchemaNode => ({ type: 'integer', minimum: 0, title });

/**
 * A single media-asset reference. Mirrors the backend `mediaRef()` (a nullable
 * string holding a media UUID; the public read hydrates a resolved sibling). The
 * `format: 'uuid'` makes `detectWidget` render the asset picker offline, matching
 * the live form — the `*_id` name convention would also catch it, but declaring the
 * format keeps the fallback self-describing.
 */
const media = (title: string, hint?: string): JSONSchemaNode => ({
  type: 'string',
  format: 'uuid',
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/**
 * A string that references another collection by *slug* (backend `refSlug()`), so
 * `detectWidget` renders the reference picker (a live dropdown of that collection's
 * items) instead of a raw text box. The persisted value is the canonical slug.
 */
const refSlugField = (
  collection: string,
  title: string,
  hint?: string,
): JSONSchemaNode => ({
  type: 'string',
  'x-ref-slug': collection,
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/**
 * A repeatable fieldset: an array of objects sharing one property shape (backend
 * `arr(obj({…}))`), e.g. a service's `benefits` ({metric, label}) or a case study's
 * `results` / `gallery`. Renders as the schema-form's add/remove item list.
 */
const objectArray = (
  title: string,
  properties: Record<string, JSONSchemaNode>,
  hint?: string,
): JSONSchemaNode => ({
  type: 'array',
  title,
  ...(hint ? { 'x-hint': hint } : {}),
  items: { type: 'object', properties },
});

// ── Field-note building blocks (mirror of `fieldNoteDocSchema`) ───────────────
/** Author byline shown on the article (name + role, bilingual). */
const AUTHOR_BLOCK: JSONSchemaNode = {
  type: 'object',
  title: 'fields.author',
  properties: {
    name: { ...I18N, title: 'fields.authorName' },
    role: { ...I18N, title: 'fields.authorRole' },
  },
};

/**
 * The hero image. Mirrors the backend `media` object: a path + bilingual alt +
 * intrinsic dimensions. The backend schema (authoritative) drives the live media
 * picker; this fallback exposes the same field names.
 */
const HERO_BLOCK: JSONSchemaNode = {
  type: 'object',
  title: 'fields.hero',
  properties: {
    src: { type: 'string', title: 'fields.heroSrc', 'x-hint': 'fields.heroSrcHint' },
    alt: { ...I18N, title: 'fields.altText' },
    width: { type: 'integer', title: 'fields.width' },
    height: { type: 'integer', title: 'fields.height' },
  },
};

/**
 * One block of article body — the permissive superset of the typed union
 * (paragraph | subheading | list | pullquote | aside | figure). Every member field
 * is optional, gated by `type`; only `type` is closed so an unknown block can never
 * reach a renderer with no case for it. Authored as two parallel locale arrays.
 */
const BODY_BLOCK: JSONSchemaNode = {
  type: 'object',
  title: 'fields.bodyBlock',
  properties: {
    type: enumField(FIELD_NOTE_BLOCK_TYPES, 'fields.blockType'),
    text: { type: 'string', title: 'fields.blockText', 'x-hint': 'fields.blockTextHint' },
    items: { type: 'array', title: 'fields.blockItems', items: { type: 'string' } },
    attribution: { type: 'string', title: 'fields.blockAttribution', 'x-hint': 'fields.blockAttributionHint' },
    caption: { type: 'string', title: 'fields.blockCaption' },
  },
};

/** A bilingual string list {en:[…], ar:[…]} — the i18nArr backend shape. */
const I18N_STRING_ARRAY: JSONSchemaNode = {
  type: 'object',
  properties: {
    en: { type: 'array', title: 'fields.bodyEn', items: { type: 'string' } },
    ar: { type: 'array', title: 'fields.bodyAr', items: { type: 'string' } },
  },
};

/** Bilingual list helper with a per-field title (mirrors the backend i18nArr). */
const i18nStringArray = (title: string, hint?: string): JSONSchemaNode => ({
  ...I18N_STRING_ARRAY,
  title,
  ...(hint ? { 'x-hint': hint } : {}),
});

/** Localized body: two parallel block arrays {en:[block], ar:[block]} (like field-notes). */
const LOCALIZED_BODY: JSONSchemaNode = {
  type: 'object',
  title: 'fields.body',
  'x-hint': 'fields.bodyHint',
  properties: {
    en: { type: 'array', title: 'fields.bodyEn', items: BODY_BLOCK },
    ar: { type: 'array', title: 'fields.bodyAr', items: BODY_BLOCK },
  },
};

/** The article's closing call to action (its single next step → /start). */
const FINAL_CTA_LEAF: JSONSchemaNode = {
  type: 'object',
  properties: {
    heading: { type: 'string', title: 'fields.ctaHeading' },
    promise: { type: 'string', title: 'fields.ctaPromise' },
    primary: {
      type: 'object',
      title: 'fields.ctaPrimary',
      properties: {
        label: { type: 'string', title: 'fields.ctaLabel' },
        href: { type: 'string', title: 'fields.ctaHref' },
      },
    },
    secondary: {
      type: 'object',
      title: 'fields.ctaSecondary',
      properties: {
        label: { type: 'string', title: 'fields.ctaLabel' },
        href: { type: 'string', title: 'fields.ctaHref' },
      },
    },
    marginNote: { type: 'string', title: 'fields.ctaMarginNote' },
  },
};

export const COLLECTIONS: CollectionConfig[] = [
  {
    // The thought-leadership essays. A slug-addressed, SEO-bearing collection: the
    // list feeds the blog index, by-slug feeds the article page, and the
    // slugs feed generateStaticParams + the sitemap. The display `title` is the
    // editor's dedicated field (mirrored into doc.title by the service), so it is
    // absent here — one title control, like every collection. No facets: a field
    // note's `category` is a localized label, not a queryable key (Phase-1 D4).
    key: 'field-notes',
    navKey: 'fieldNotes',
    label: 'collections.fieldNotes.label',
    singularLabel: 'collections.fieldNotes.singular',
    identType: 'slug',
    hasSeo: true,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.fieldNoteContent',
      $defs: SCHEMA_DEFS,
      properties: {
        // Index-card fields (what the Field Notes list renders).
        category: { ...I18N, title: 'fields.category', 'x-hint': 'fields.fieldNoteCategoryHint' },
        topic: enumField(INSIGHT_TOPICS, 'fields.topic', 'fields.topicHint'),
        excerpt: { ...I18N, title: 'fields.excerpt', 'x-hint': 'fields.excerptHint' },
        reading_time: { ...I18N, title: 'fields.readingTime', 'x-hint': 'fields.readingTimeHint' },
        date: { ...I18N, title: 'fields.displayDate', 'x-hint': 'fields.displayDateHint' },
        date_iso: { type: 'string', title: 'fields.dateIso', 'x-hint': 'fields.dateIsoHint' },
        updated_iso: { type: 'string', title: 'fields.updatedIso', 'x-hint': 'fields.updatedIsoHint' },
        featured: { type: 'boolean', title: 'fields.featured', 'x-hint': 'fields.fieldNoteFeaturedHint' },
        hero: HERO_BLOCK,
        // Article fields (what the reading page renders).
        question: { ...I18N, title: 'fields.question', 'x-hint': 'fields.questionHint' },
        lede: { ...I18N_RICH, title: 'fields.lede', 'x-hint': 'fields.ledeHint' },
        author: AUTHOR_BLOCK,
        body: {
          type: 'object',
          title: 'fields.body',
          'x-hint': 'fields.bodyHint',
          properties: {
            en: { type: 'array', title: 'fields.bodyEn', items: BODY_BLOCK },
            ar: { type: 'array', title: 'fields.bodyAr', items: BODY_BLOCK },
          },
        },
        final_cta: {
          type: 'object',
          title: 'fields.finalCta',
          'x-hint': 'fields.finalCtaHint',
          properties: {
            en: { ...FINAL_CTA_LEAF, title: 'fields.finalCtaEn' },
            ar: { ...FINAL_CTA_LEAF, title: 'fields.finalCtaAr' },
          },
        },
      },
    },
  },
  {
    // The two-layer service offer (core ⇄ add-on). Slug-addressed, SEO-bearing:
    // the list feeds the services overview, by-slug feeds the detail page. Backend
    // schema entity: `service`. `title` is the editor's dedicated field.
    key: 'services',
    navKey: 'services',
    label: 'collections.services.label',
    singularLabel: 'collections.services.singular',
    identType: 'slug',
    hasSeo: true,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.serviceContent',
      $defs: SCHEMA_DEFS,
      properties: {
        // Card fields (services overview + homepage teaser).
        short_title: { ...I18N, title: 'fields.shortTitle', 'x-hint': 'fields.shortTitleHint' },
        description: { ...I18N, title: 'fields.description' },
        icon: enumField(SERVICE_ICONS, 'fields.icon', 'fields.serviceIconHint'),
        order: { type: 'integer', title: 'fields.order' },
        tier: enumField(SERVICE_TIERS, 'fields.serviceTier', 'fields.serviceTierHint'),
        // Detail-page fields.
        features: i18nStringArray('fields.features', 'fields.featuresHint'),
        // `metric` is a non-localized display token ("80%", "3x"); `label` is localized.
        benefits: objectArray(
          'fields.benefits',
          {
            metric: { type: 'string', title: 'fields.metric', 'x-hint': 'fields.metricHint' },
            label: { ...I18N, title: 'fields.label' },
          },
          'fields.benefitsHint',
        ),
        faq: objectArray('fields.faq', {
          q: { ...I18N, title: 'fields.faqQuestion' },
          a: { ...I18N, title: 'fields.faqAnswer' },
        }),
        hero_id: media('fields.heroImage', 'fields.serviceHeroHint'),
        body: LOCALIZED_BODY,
      },
    },
  },
  {
    // The proof layer — one reusable case-study structure. Slug-addressed,
    // SEO-bearing. Backend schema entity: `case_study`. Results / testimonial ship
    // as marked placeholders until verified.
    key: 'case-studies',
    navKey: 'caseStudies',
    label: 'collections.caseStudies.label',
    singularLabel: 'collections.caseStudies.singular',
    identType: 'slug',
    hasSeo: true,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.caseStudyContent',
      $defs: SCHEMA_DEFS,
      properties: {
        // `client` is a plain brand string (not localized); `built_for` / `industry`
        // are the localized display labels.
        client: { type: 'string', title: 'fields.client' },
        built_for: { ...I18N, title: 'fields.builtFor' },
        industry: { ...I18N, title: 'fields.industry' },
        // Facet keys — stored value must equal a canonical enum key (public filters).
        industry_key: enumField(INDUSTRY_KEYS, 'fields.industryKey', 'fields.industryKeyHint'),
        kind: enumField(PORTFOLIO_KINDS, 'fields.kind', 'fields.kindHint'),
        year: { type: 'integer', title: 'fields.year' },
        device: enumField(DEVICE_TYPES, 'fields.device', 'fields.deviceHint'),
        challenge: { ...I18N, title: 'fields.challenge' },
        solution: { ...I18N, title: 'fields.solution' },
        // `metric` is a non-localized proof token ("3-in-1", "100%"); `label` localized.
        results: objectArray('fields.results', {
          metric: { type: 'string', title: 'fields.metric', 'x-hint': 'fields.metricHint' },
          label: { ...I18N, title: 'fields.label' },
        }),
        // Each item references a service by slug (live reference picker).
        service_slugs: {
          type: 'array',
          title: 'fields.serviceSlugs',
          'x-hint': 'fields.serviceSlugsHint',
          items: refSlugField('services', 'fields.service'),
        },
        tech: {
          type: 'array',
          title: 'fields.tech',
          'x-hint': 'fields.techHint',
          items: { type: 'string' },
        },
        featured: { type: 'boolean', title: 'fields.featured', 'x-hint': 'fields.caseFeaturedHint' },
        logo_id: media('fields.logo'),
        thumbnail_en_id: media('fields.thumbnailEn', 'fields.thumbnailEnHint'),
        thumbnail_ar_id: media('fields.thumbnailAr', 'fields.thumbnailArHint'),
        url: { type: 'string', title: 'fields.liveUrl', 'x-hint': 'fields.liveUrlHint' },
        // `image_id` hydrates a resolved `image` sibling on public read; caption localized.
        gallery: objectArray(
          'fields.gallery',
          {
            image_id: media('fields.galleryImage'),
            caption: { ...I18N, title: 'fields.galleryCaption' },
          },
          'fields.galleryHint',
        ),
      },
    },
  },
  {
    // The public price card (/pricing) and the estimator's input. Key-addressed,
    // no SEO. Backend schema entity: `package`. `name` is the editor's dedicated
    // title field. Figures are whole USD; the backend rejects negative/fractional.
    key: 'packages',
    navKey: 'packages',
    label: 'collections.packages.label',
    singularLabel: 'collections.packages.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.packageContent',
      $defs: SCHEMA_DEFS,
      properties: {
        kind: enumField(PACKAGE_KINDS, 'fields.packageKind', 'fields.packageKindHint'),
        category: enumField(PACKAGE_CATEGORIES, 'fields.packageCategory', 'fields.packageCategoryHint'),
        ideal_for: { ...I18N, title: 'fields.idealFor' },
        price_from: money('fields.priceFrom', 'fields.priceFromHint'),
        price_to: money('fields.priceTo', 'fields.priceToHint'),
        price_unit: enumField(PRICE_UNITS, 'fields.priceUnit'),
        investment: { ...I18N, title: 'fields.investment', 'x-hint': 'fields.investmentHint' },
        timeline: { ...I18N, title: 'fields.timeline' },
        inclusions: i18nStringArray('fields.inclusions'),
        recommended: { type: 'boolean', title: 'fields.recommended', 'x-hint': 'fields.recommendedHint' },
        cta: {
          type: 'object',
          title: 'fields.cta',
          properties: {
            label: { ...I18N, title: 'fields.ctaLabelField' },
            href: { type: 'string', title: 'fields.ctaHref' },
          },
        },
        estimator: {
          type: 'object',
          title: 'fields.estimator',
          'x-hint': 'fields.estimatorHint',
          properties: {
            role: enumField(ESTIMATOR_ROLES, 'fields.estimatorRole'),
            pages_included: count('fields.pagesIncluded'),
            pages_max: count('fields.pagesMax'),
            extra_page_price: count('fields.extraPagePrice'),
            bilingual_included: { type: 'boolean', title: 'fields.bilingualIncluded' },
            bilingual_uplift_pct: count('fields.bilingualUpliftPct'),
            mobile_included: { type: 'boolean', title: 'fields.mobileIncluded', 'x-hint': 'fields.mobileIncludedHint' },
          },
        },
      },
    },
  },
  {
    // The client roster. Key-addressed, no SEO. Backend schema entity: `client`.
    // `name` is the editor's dedicated title field.
    key: 'clients',
    navKey: 'clients',
    label: 'collections.clients.label',
    singularLabel: 'collections.clients.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.clientContent',
      $defs: SCHEMA_DEFS,
      properties: {
        logo_id: media('fields.logo'),
        industry: { ...I18N, title: 'fields.industry' },
        country: { ...I18N, title: 'fields.country' },
        type: enumField(CLIENT_TYPES, 'fields.clientType'),
        type_label: { ...I18N, title: 'fields.typeLabel' },
        website: { type: 'string', title: 'fields.website' },
        case_study_slug: refSlugField('case-studies', 'fields.caseStudySlug', 'fields.caseStudySlugHint'),
      },
    },
  },
  {
    // The about-page team. Key-addressed, no SEO. Backend schema entity:
    // `team_member`. `name` is the editor's dedicated title field.
    key: 'team-members',
    navKey: 'teamMembers',
    label: 'collections.teamMembers.label',
    singularLabel: 'collections.teamMembers.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.teamMemberContent',
      $defs: SCHEMA_DEFS,
      properties: {
        role: { ...I18N, title: 'fields.role' },
        department: enumField(DEPARTMENTS, 'fields.department'),
        note: { ...I18N, title: 'fields.note', 'x-hint': 'fields.teamNoteHint' },
        portrait_id: media('fields.teamPhoto', 'fields.teamPhotoHint'),
        open: { type: 'boolean', title: 'fields.open', 'x-hint': 'fields.openHint' },
        email: { type: 'string', title: 'fields.email' },
        linkedin: { type: 'string', title: 'fields.linkedin', 'x-hint': 'fields.linkedinHint' },
      },
    },
  },
  {
    // Client testimonials. Key-addressed, no SEO. Backend schema entity:
    // `testimonial`. The person's `name` is the editor's dedicated title field.
    key: 'testimonials',
    navKey: 'testimonials',
    label: 'collections.testimonials.label',
    singularLabel: 'collections.testimonials.singular',
    identType: 'key',
    hasSeo: false,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.testimonialContent',
      $defs: SCHEMA_DEFS,
      properties: {
        quote: { ...I18N, title: 'fields.quote' },
        role: { ...I18N, title: 'fields.role' },
        // `company` is a plain brand string; `metric` a non-localized proof token.
        company: { type: 'string', title: 'fields.company' },
        metric: { type: 'string', title: 'fields.metric', 'x-hint': 'fields.metricHint' },
        logo_id: media('fields.logo'),
        case_study_slug: refSlugField('case-studies', 'fields.caseStudySlug', 'fields.caseStudySlugHint'),
        order: { type: 'integer', title: 'fields.order' },
      },
    },
  },
  {
    // Site-wide image slots (logo, homepage figure, share image). A fixed register
    // of records — edit only — each holding one media reference the website reads
    // by `key`. Backend schema entity: `site_asset`.
    key: 'site-assets',
    navKey: 'siteAssets',
    label: 'collections.siteAssets.label',
    singularLabel: 'collections.siteAssets.singular',
    identType: 'key',
    hasSeo: false,
    editOnly: true,
    facets: [],
    docSchema: {
      type: 'object',
      title: 'fields.siteAssetContent',
      $defs: SCHEMA_DEFS,
      properties: {
        image_id: { type: 'string', format: 'uuid', title: 'fields.image', 'x-hint': 'fields.imageHint' },
        alt: { ...I18N, title: 'fields.altText' },
      },
    },
  },
];

export function getCollection(key: string): CollectionConfig | undefined {
  return COLLECTIONS.find((c) => c.key === key);
}
